/* ================================================================ */
// POPUP COMPONENT | Popup.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {useState, useRef, useEffect, useCallback} from 'react';
import useReactRouter from 'use-react-router';
import PropTypes from 'prop-types';
import { isNullOrUndefined } from 'util';
// Material UI
import Dialog from '@material-ui/core/Dialog';
import Fab from '@material-ui/core/Fab';
import Icon from '@material-ui/core/Icon';
import makeStyles from '@material-ui/styles/makeStyles';
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        zIndex: 10,
        //position: 'absolute',
        backgroundColor: theme.palette.background.default,
        //right: '3rem',
        //top: '5rem',
        //width: '75%',
        //maxWidth: '75%',
        overflow: 'initial'
    },
    video: {
        width: '100%',
        height: '100%'
    },
    image: {
        objectFit: 'contain',
        width: '100%',
        height: '100%'
    },
    loadingContainer: {
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    closeButton: {
        zIndex: 10,
        backgroundColor: theme.palette.secondary.contrastText,
        color: theme.palette.secondary.main,
        position: 'absolute',
        width: '3.5rem',
        height: '3.5rem',
        left: '-1.25rem',
        top: '-1.25rem'
    }
}));
// TODO: IMPLEMENT MEDIA SRC GET IN POPUP WRAPPER
/* ========================================== */
/**
 * Video Popup
 * -----------
 * Outputs a fullscreen dialog for video media
 * display
 *
 * @param {object} classes style classes
 * @param {string} content html content
 */
/* ========================================== */
const Popup = ({type, onClose, ...props}) => {
    const videoTimer = useRef();
    const video = useRef();
    const [open, setOpen] = useState(false);
    const classes = useStyles();
    const {history} = useReactRouter();
    const query = new URLSearchParams(location.search);
    const media = query.get('media') || null;
    useEffect(() => {
        console.debug(`${Popup.displayName} | `, 'url query:', query);
        setOpen(!isNullOrUndefined(media));
    }, [media]);
    /* ======================== */
    // HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle Close
     * ------------
     * Triggers closing functions and
     * sets open state to false
     *
     * @param {object} onClose onClose callback schema
     */
    const handleClose = useCallback(()=>{
        // Execute all listed callbacks
        resolveCallbacks(onClose).forEach(callback=>callback());
        setOpen(false);
    }, [onClose, resolveCallbacks]);
    /* ========================================== */
    /**
     * Handle Video
     * ------------
     * Toggles video play/pause on a timer delay
     */
    function handleVideo() {
        if(videoTimer.current) {
            clearTimeout(videoTimer.current);
        }
        videoTimer.current = setTimeout(()=>{
            video.current.paused ? video.current.play() : video.current.pause();
        }, 100);
    }
    /* ========================================== */
    /**
     * Resolve Callbacks
     * -----------------
     * Resolve listed callback key and parameters with
     * existing callback function
     *
     * @param {object} callbacks callback schema for function
     */
    const resolveCallbacks = useCallback((callbacks)=>{
        return Object.keys(callbacks).map(key=>{
            switch(key) {
            case 'redirect':
                return history.push(callbacks[key]);
            default:
                return ()=>console.warn(`No callback found for ${key}`);
            }
        });
    }, [history]);
    /* ======================== */
    // RENDER
    /* ======================== */
    return (
        <Dialog disableBackdropClick={true} transitionDuration={800} open={open} onClose={handleClose} classes={{paper: classes.root}}>
            {type === 'video' ?
                <video
                    ref={video}
                    key="popup-video"
                    alt="popup-video"
                    className={classes.video}
                    controls="controls"
                    onTouchStart={handleVideo}
                    //autoPlay
                    loop
                >
                    <source src={media} type="video/mp4" />
                </video>
                : 
                <img 
                    className={classes.image} 
                    alt="popup-content" 
                    src={media}
                />
            }
            <Fab onClick={handleClose} aria-label="Close" className={classes.closeButton}>
                <Icon>close</Icon>
            </Fab>
        </Dialog>
    );
};
/* ======================== */
// PROPS
/* ======================== */
Popup.propTypes = {
    category: PropTypes.string.isRequired,
    media: PropTypes.string,
    type: PropTypes.oneOf(['video', 'image']),
    content: PropTypes.string,
    onClose: PropTypes.shape({
        redirect: PropTypes.string
    })
};
Popup.defaultProps = {
    media: '',
    mediaType: 'video',
    content: ''
};
Popup.displayName = 'Popup';
/* ========================================== */
// Export
/* ========================================== */
export default Popup;